/**
 * The article editor (Admin → Articles): rich text on TipTap, storing our Markdown (articleMarkdown.ts). Paragraphs,
 * subheads, lists, bold, italic and links are edited in place; our blocks are nodes with their own views:
 *   - a quote (the words, and who said them, from the transcript);
 *   - a figure (an image with alt text and a caption);
 *   - a clip (its start, end and caption editable in place; "Open video" scrubs the video and updates the clip), a vote
 *     widget and a chart (editable in the chart picker), drawn by the Worker exactly as the public page draws them (/render).
 * Blocks can be dragged in from the Sources panel (dataTransfer "text/x-omb-block": Markdown) and dropped where they
 * should go; image files dropped on the text are uploaded and become figures; pasted Markdown becomes blocks.
 * With `collab` (admin/collab.ts: the article's room), the document is the shared Yjs one: everyone's edits merge as
 * they type, carets show who is where, and undo is per editor (TipTap Collaboration + CollaborationCaret).
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type ComponentType, type ReactNode } from "react";
import { EditorContent, NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer, useEditor, type ReactNodeViewProps } from "@tiptap/react";
import { Node, mergeAttributes, type Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
import type * as Y from "yjs";
import type { WebsocketProvider } from "y-websocket";
import { Bold, Heading2, Heading3, Italic, Link2, List, Quote, Redo2, Undo2 } from "lucide-react";
import { docToMarkdown, looksLikeMarkdown, markdownToDoc } from "./articleMarkdown";

export const OMB_DRAG = "text/x-omb-block";

export interface EditorHandle {
  /** Insert Markdown block(s) at the cursor. */
  insertMarkdown(md: string): void;
  /** The selected text (plain), or "" when nothing is selected. */
  selectionText(): string;
  /** Replace the selection with text (or, with nothing selected, replace the whole body with Markdown). */
  replaceSelection(text: string): void;
}

/** A clip already in the text, handed to the video popup so "Update clip" changes it in place. */
export interface ExistingClip { start: string; end: string; caption: string; apply: (c: { start: string; end: string; caption: string }) => void }

interface Options {
  render: (md: string) => Promise<string>;
  openVideo: (meetingId: string, start: number, existing?: ExistingClip) => void;
  /** Open the chart picker on a spec (the JSON inside [[chart …]]); `apply` gets the edited spec. */
  editChart: (spec: string, apply: (spec: string) => void) => void;
}

// ---------- our blocks ----------

const atom = (name: string, attrs: Record<string, { default: string }>, View: ComponentType<ReactNodeViewProps>) =>
  Node.create<Options>({
    name, group: "block", atom: true, draggable: true, selectable: true,
    addOptions() { return { render: async () => "", openVideo: () => {}, editChart: () => {} }; },
    addAttributes() { return attrs; },
    parseHTML() { return [{ tag: `div[data-${name}]` }]; },
    renderHTML({ HTMLAttributes }) { return ["div", mergeAttributes({ [`data-${name}`]: "" }, HTMLAttributes)]; },
    addNodeView() { return ReactNodeViewRenderer(View); },
  });

const seconds = (t: string) => t.split(":").reduce((a, x) => a * 60 + Number(x), 0);
const TIME = /^\d{1,2}(:\d{2}){0,2}$/;

/** A block the Worker renders (clip, vote widget, chart): the page's own HTML, fetched once per Markdown. */
function RenderedView({ md, extension, selected, children }: { md: string; extension: ReactNodeViewProps["extension"]; selected: boolean; children?: ReactNode }) {
  const [html, setHtml] = useState<string | null>(null);
  useEffect(() => { let on = true; (extension.options as Options).render(md).then((h) => { if (on) setHtml(h); }); return () => { on = false; }; }, [md]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <NodeViewWrapper className={`omb-ed-block${selected ? " is-selected" : ""}`} data-drag-handle>
      {children}
      {html == null ? <p className="muted art-small">Drawing…</p> : html ? <div className="omb-ed-rendered" dangerouslySetInnerHTML={{ __html: html }} /> : <p className="muted art-small">Nothing to show for <code>{md}</code>.</p>}
    </NodeViewWrapper>
  );
}

/** A clip's start or end, edited as h:mm:ss and committed when it parses. */
function TimeInput({ value, label, onCommit }: { value: string; label: string; onCommit: (v: string) => void }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  const commit = () => { if (TIME.test(v.trim())) onCommit(v.trim()); else setV(value); };
  return <input className="text-input omb-ed-block__time" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }} aria-label={label} placeholder="h:mm:ss" />;
}

const ClipView = (p: ReactNodeViewProps) => {
  const a = p.node.attrs as { meetingId: string; start: string; end: string; caption: string };
  const md = `[[clip ${a.meetingId} ${a.start}${a.end ? `-${a.end}` : ""}${a.caption ? ` "${a.caption.replace(/"/g, "'")}"` : ""}]]`;
  const opts = p.extension.options as Options;
  return (
    <RenderedView md={md} extension={p.extension} selected={p.selected}>
      <div className="omb-ed-block__bar">
        <span className="art-small muted">Clip</span>
        <TimeInput value={a.start} label="Clip start" onCommit={(start) => p.updateAttributes({ start })} />
        <span className="muted">–</span>
        <TimeInput value={a.end} label="Clip end" onCommit={(end) => p.updateAttributes({ end })} />
        <input className="text-input omb-ed-block__caption" value={a.caption} onChange={(e) => p.updateAttributes({ caption: e.target.value })} placeholder="Caption" aria-label="Clip caption" />
        <button type="button" className="link-button" onClick={() => opts.openVideo(a.meetingId, seconds(a.start), { start: a.start, end: a.end, caption: a.caption, apply: (c) => p.updateAttributes(c) })}>Open video</button>
        <button type="button" className="link-button" onClick={() => p.deleteNode()}>Remove</button>
      </div>
    </RenderedView>
  );
};
const VoteView = (p: ReactNodeViewProps) => (
  <RenderedView md={`[[vote ${p.node.attrs.record}]]`} extension={p.extension} selected={p.selected}>
    <div className="omb-ed-block__bar"><span className="art-small muted">How each ward voted · {p.node.attrs.record}</span><button type="button" className="link-button" onClick={() => p.deleteNode()}>Remove</button></div>
  </RenderedView>
);
const ChartView = (p: ReactNodeViewProps) => (
  <RenderedView md={`[[chart ${p.node.attrs.spec}]]`} extension={p.extension} selected={p.selected}>
    <div className="omb-ed-block__bar">
      <span className="art-small muted">Chart</span>
      <button type="button" className="link-button" onClick={() => (p.extension.options as Options).editChart(p.node.attrs.spec, (spec) => p.updateAttributes({ spec }))}>Edit</button>
      <button type="button" className="link-button" onClick={() => p.deleteNode()}>Remove</button>
    </div>
  </RenderedView>
);
const FigureView = (p: ReactNodeViewProps) => {
  const a = p.node.attrs as { src: string; alt: string; caption: string };
  return (
    <NodeViewWrapper as="figure" className={`omb-ed-figure${p.selected ? " is-selected" : ""}`} data-drag-handle>
      <img src={a.src} alt={a.alt} />
      <input className="text-input" value={a.caption} onChange={(e) => p.updateAttributes({ caption: e.target.value })} placeholder="Caption (who, where, when. Credit.)" aria-label="Caption" />
      <input className="text-input art-small" value={a.alt} onChange={(e) => p.updateAttributes({ alt: e.target.value })} placeholder="Alt text" aria-label="Alt text" />
      <button type="button" className="link-button" onClick={() => p.deleteNode()}>Remove</button>
    </NodeViewWrapper>
  );
};
const QuoteView = (p: ReactNodeViewProps) => (
  <NodeViewWrapper as="figure" className={`omb-ed-quote${p.selected ? " is-selected" : ""}`}>
    <blockquote><NodeViewContent /></blockquote>
    <input className="text-input omb-ed-quote__who" value={p.node.attrs.who} onChange={(e) => p.updateAttributes({ who: e.target.value })} placeholder="Who said it, where, when" aria-label="Attribution" contentEditable={false} />
  </NodeViewWrapper>
);

const OmbClip = atom("ombClip", { meetingId: { default: "" }, start: { default: "0:00" }, end: { default: "" }, caption: { default: "" } }, ClipView);
const OmbVote = atom("ombVote", { record: { default: "" } }, VoteView);
const OmbChart = atom("ombChart", { spec: { default: "" } }, ChartView);
const OmbFigure = atom("ombFigure", { src: { default: "" }, alt: { default: "" }, caption: { default: "" } }, FigureView);
const OmbQuote = Node.create({
  name: "ombQuote", group: "block", content: "paragraph+", defining: true,
  addAttributes() { return { who: { default: "" } }; },
  parseHTML() { return [{ tag: "figure[data-omb-quote]" }]; },
  renderHTML() { return ["figure", { "data-omb-quote": "" }, 0]; },
  addNodeView() { return ReactNodeViewRenderer(QuoteView); },
});

// ---------- the editor ----------

interface Props extends Options {
  value: string; onChange: (md: string) => void;
  upload: (file: Blob, name?: string) => Promise<string | null>;
  /** The article's shared document and room; the editor binds to it instead of `value`. */
  collab?: { doc: Y.Doc; provider: WebsocketProvider; user: { name: string; color: string } };
  /** With collab: `value` was edited elsewhere on this page (the Markdown view) and should replace the shared document. */
  pushValueOnCreate?: boolean;
}

export const ArticleEditor = forwardRef<EditorHandle, Props>(function ArticleEditor({ value, onChange, upload, render, openVideo, editChart, collab, pushValueOnCreate }, ref) {
  const last = useRef(value);
  const [linkBar, setLinkBar] = useState<string | null>(null);
  const insertAt = (editor: Editor, pos: number | { from: number; to: number }, md: string) => {
    const nodes = markdownToDoc(md).content;
    editor.chain().focus().insertContentAt(pos, nodes as never).run();
  };
  const opts = { render, openVideo, editChart };
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, blockquote: false, codeBlock: false, code: false, strike: false, horizontalRule: false, link: { openOnClick: false, autolink: true, defaultProtocol: "https" }, ...(collab ? { undoRedo: false as const } : {}) }),
      Placeholder.configure({ placeholder: "Write. ## for a subhead. Drag clips, quotes and images in from Sources, or paste Markdown." }),
      OmbQuote, OmbFigure.configure(opts), OmbClip.configure(opts), OmbVote.configure(opts), OmbChart.configure(opts),
      ...(collab ? [Collaboration.configure({ document: collab.doc }), CollaborationCaret.configure({ provider: collab.provider, user: collab.user })] : []),
    ],
    // Shared: the document comes from the room (already synced when this mounts), so the page's body follows it.
    content: collab ? undefined : (markdownToDoc(value) as never),
    onCreate: ({ editor }) => {
      if (!collab) return;
      const shared = docToMarkdown(editor.getJSON() as never);
      if (pushValueOnCreate && shared.trim() !== value.trim()) editor.commands.setContent(markdownToDoc(value) as never);
      const md = docToMarkdown(editor.getJSON() as never);
      last.current = md;
      if (md !== value) onChange(md);
    },
    editorProps: {
      attributes: { class: "omb-ed", "aria-label": "Article" },
      handleDrop(view, event) {
        const md = event.dataTransfer?.getData(OMB_DRAG) || "";
        const files = [...(event.dataTransfer?.files ?? [])].filter((f) => f.type.startsWith("image/"));
        if (!md && !files.length) return false;
        event.preventDefault();
        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos ?? view.state.selection.to;
        if (md) insertAt(editor!, pos, md);
        for (const f of files) upload(f, f.name).then((url) => { if (url) insertAt(editor!, pos, `![${f.name.replace(/\.\w+$/, "")}](${url} "Caption")`); });
        return true;
      },
      // Pasted Markdown (ours, or any with subheads, lists, links or emphasis) becomes blocks, not a paragraph of symbols.
      handlePaste(view, event) {
        const text = event.clipboardData?.getData("text/plain") || "";
        if (!text.trim() || !looksLikeMarkdown(text)) return false;
        event.preventDefault();
        const { from, to } = view.state.selection;
        insertAt(editor!, { from, to }, text);
        return true;
      },
    },
    onUpdate: ({ editor }) => { const md = docToMarkdown(editor.getJSON() as never); last.current = md; onChange(md); },
  });
  useEffect(() => {
    if (!editor || value === last.current) return;
    last.current = value;
    editor.commands.setContent(markdownToDoc(value) as never, { emitUpdate: false });
  }, [value, editor]);
  useImperativeHandle(ref, () => ({
    insertMarkdown: (md) => { if (editor) insertAt(editor, editor.state.selection.to, md); },
    selectionText: () => { if (!editor) return ""; const { from, to } = editor.state.selection; return from === to ? "" : editor.state.doc.textBetween(from, to, "\n"); },
    replaceSelection: (text) => {
      if (!editor) return;
      const { from, to } = editor.state.selection;
      if (from === to) editor.commands.setContent(markdownToDoc(text) as never);
      else editor.chain().focus().insertContentAt({ from, to }, text).run();
    },
  }), [editor]);
  if (!editor) return null;
  const B = ({ on, label, active, disabled, children }: { on: () => void; label: string; active?: boolean; disabled?: boolean; children: ReactNode }) =>
    <button type="button" className="omb-ed-tb__btn" aria-label={label} title={label} aria-pressed={!!active} disabled={disabled} onMouseDown={(e) => { e.preventDefault(); on(); }}>{children}</button>;
  const applyLink = (href: string) => {
    const h = href.trim();
    if (!h) editor.chain().focus().extendMarkRange("link").unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: /^(https?:\/\/|\/|mailto:)/.test(h) ? h : `https://${h}` }).run();
    setLinkBar(null);
  };
  return (
    <div className="omb-ed-wrap">
      <div className="omb-ed-tb" role="toolbar" aria-label="Formatting">
        <B label="Undo" disabled={!editor.can().undo()} on={() => editor.chain().focus().undo().run()}><Undo2 size={14} aria-hidden /></B>
        <B label="Redo" disabled={!editor.can().redo()} on={() => editor.chain().focus().redo().run()}><Redo2 size={14} aria-hidden /></B>
        <B label="Bold" active={editor.isActive("bold")} on={() => editor.chain().focus().toggleBold().run()}><Bold size={14} aria-hidden /></B>
        <B label="Italic" active={editor.isActive("italic")} on={() => editor.chain().focus().toggleItalic().run()}><Italic size={14} aria-hidden /></B>
        <B label="Link" active={editor.isActive("link")} on={() => setLinkBar(linkBar == null ? ((editor.getAttributes("link").href as string | undefined) || "") : null)}><Link2 size={14} aria-hidden /></B>
        <B label="Subhead" active={editor.isActive("heading", { level: 2 })} on={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={14} aria-hidden /></B>
        <B label="Small subhead" active={editor.isActive("heading", { level: 3 })} on={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={14} aria-hidden /></B>
        <B label="List" active={editor.isActive("bulletList")} on={() => editor.chain().focus().toggleBulletList().run()}><List size={14} aria-hidden /></B>
        <B label="Quote" on={() => insertAt(editor, editor.state.selection.to, '> "…"\n> — Who, where, when')}><Quote size={14} aria-hidden /></B>
      </div>
      {linkBar != null && (
        <form className="omb-ed-linkbar" onSubmit={(e) => { e.preventDefault(); applyLink(linkBar); }}>
          <input className="text-input" value={linkBar} onChange={(e) => setLinkBar(e.target.value)} placeholder="https://… or /matter/…" aria-label="Link to" autoFocus />
          <button type="submit" className="btn btn--primary btn--small">Link</button>
          {editor.isActive("link") && <button type="button" className="btn btn--secondary btn--small" onClick={() => applyLink("")}>Remove link</button>}
          <button type="button" className="link-button" onClick={() => setLinkBar(null)}>Cancel</button>
        </form>
      )}
      <EditorContent editor={editor} />
    </div>
  );
});
